import { LANG_PROP_NAME } from './constants';
import type { LangDto } from './dtos/lang.dto';
import { jsonParseSafe, parseSearchParams } from './helpers';
import { ALLOWED_LOCALES, DEFAULT_LOCALE } from './locale.constants';

export type TResolveLocaleInput = {
  queryLang?: string;
  cookieLang?: Pick<LangDto, 'current' | 'locale'>;
  userLang?: Pick<LangDto, 'current' | 'locale'>;
  browserLocale?: string;
};

export function normalizeLocale(raw: string | undefined | null): string | null {
  if (raw == null) {
    return null;
  }

  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }

  const base = trimmed.split('-')[0] ?? trimmed;
  if (ALLOWED_LOCALES.has(base)) {
    return base;
  }

  if (ALLOWED_LOCALES.has(trimmed)) {
    return trimmed;
  }

  return null;
}

export function resolveLocale(input: TResolveLocaleInput): string {
  const fromQuery = normalizeLocale(input.queryLang);
  if (fromQuery) {
    return fromQuery;
  }

  const fromCookie = normalizeLocale(input.cookieLang?.current);
  if (fromCookie) {
    return fromCookie;
  }

  const fromUser = normalizeLocale(input.userLang?.current);
  if (fromUser) {
    return fromUser;
  }

  const browserSource = input.cookieLang?.locale ?? input.browserLocale;
  const fromBrowser = normalizeLocale(browserSource);
  if (fromBrowser) {
    return fromBrowser;
  }

  return DEFAULT_LOCALE;
}

export function parseLangCookieRaw(
  raw: string | undefined | null,
): LangDto | undefined {
  if (!raw) {
    return undefined;
  }

  try {
    return jsonParseSafe<LangDto>(decodeURIComponent(raw)) ?? undefined;
  } catch {
    return jsonParseSafe<LangDto>(raw) ?? undefined;
  }
}

export function resolveLangDto(
  cookieLang: Pick<LangDto, 'current' | 'locale'> | undefined,
): LangDto {
  const lang = cookieLang ?? {};

  return {
    locale: lang.locale,
    current: resolveLocale({
      cookieLang: lang,
      browserLocale: lang.locale,
    }),
  };
}

export function getLangFromSearch(search: string): string | undefined {
  return parseSearchParams(search).get(LANG_PROP_NAME) ?? undefined;
}
